import { Injectable } from '@angular/core';
import {
    DO_NOTHING,
    FlyFreelyLoggingService,
    NotificationDto,
    NotificationsService,
    PersonsOrganisationDto,
    WorkTracker
} from '@flyfreely-portal-ui/flyfreely';
import { WorkspaceNavigationService } from '@flyfreely-portal-ui/workspace';
import { BehaviorSubject, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { debounceTime } from 'rxjs/operators';

interface NotificationsLoading {
    type: 'LOADING';
}

interface NotificationsLoaded {
    type: 'LOADED';
    notifications: NotificationDto[];
}

@Injectable({
    providedIn: 'root'
})
export class NotificationFeedService {
    private notificationListSubject = new BehaviorSubject<
        NotificationsLoading | NotificationsLoaded
    >({ type: 'LOADING' });
    private numberOfNewSubject = new BehaviorSubject<number>(0);
    notificationsList$ = this.notificationListSubject.asObservable();
    numberOfNew$ = this.numberOfNewSubject.asObservable();

    currentOrganisation: PersonsOrganisationDto;
    notifications: NotificationDto[];
    private websocketConnectionStatus:
        | 'connecting'
        | 'reconnected'
        | 'disconnected';
    private websocketConnected = false;

    private ngUnsubscribe$ = new Subject<void>();
    private workTracker = new WorkTracker();

    working: boolean;
    constructor(
        private notificationsService: NotificationsService,
        private workspaceNavigationService: WorkspaceNavigationService,
        private logging: FlyFreelyLoggingService
    ) {
        this.workTracker.observable
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(working => (this.working = working));

        this.setWatchers();
    }

    ngOnDestroy() {
        this.notificationListSubject.complete();
        this.numberOfNewSubject.complete();
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
    }

    setCurrentOrganisation(organisation: PersonsOrganisationDto) {
        this.currentOrganisation = organisation;
    }

    // clear out notifications on org switch to prevent populating across orgs until refresh
    onSwitchOrganisation() {
        this.notifications = [];
        this.notificationListSubject.next({ type: 'LOADING' });
        this.numberOfNewSubject.next(0);
    }

    refreshNotifications(startWebsocket = false) {
        if (this.currentOrganisation == null) {
            return;
        }
        this.notificationsService
            .findNotifications(0, this.currentOrganisation.id)
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe({
                next: notifications => {
                    this.notifications = notifications.results;
                    this.notificationListSubject.next({
                        type: 'LOADED',
                        notifications: notifications.results
                    });
                    this.numberOfNewSubject.next(
                        notifications.results.filter(n => n.read === false)
                            .length ?? 0
                    );
                    if (startWebsocket) {
                        this.subscribeToNotifications();
                    }
                },
                error: DO_NOTHING
            });
    }

    subscribeToNotifications() {
        if (this.websocketConnected) {
            // Only start the websocket connection the first time the service loads.
            return;
        }
        this.notificationsService
            .setupNotificationStream(this.currentOrganisation.id)
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe({
                next: notification => {
                    this.websocketConnected = true;
                    const existingNotifications =
                        this.notifications.concat(notification);
                    this.notifications = existingNotifications;
                    this.notificationListSubject.next({
                        type: 'LOADED',
                        notifications: existingNotifications
                    });
                    this.numberOfNewSubject.next(
                        existingNotifications.filter(n => n.read === false)
                            .length ?? 0
                    );
                },
                // The client retries connection problems itself, so this only happens on
                // fatal errors. Allow the stream to be started again on the next refresh.
                error: error => {
                    this.websocketConnected = false;
                    this.logging.error(error);
                }
            });
    }

    archiveNotification(notificationId: number) {
        this.notificationsService
            .markNotificationRead(notificationId)
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(result => {
                if (result.success === true) {
                    this.notifications.find(n => n.id === notificationId).read =
                        true;
                    this.notificationListSubject.next({
                        type: 'LOADED',
                        notifications: this.notifications
                    });
                    this.numberOfNewSubject.next(
                        this.notifications.filter(n => n.read === false)
                            .length ?? 0
                    );
                }
            })
            .add(this.workTracker.createTracker());
    }

    goToNotificationSubject(notificationId: number) {
        const notification = this.notifications.find(
            n => n.id === notificationId
        );
        const link = notification?.reference ?? '';
        if (link !== '') {
            this.workspaceNavigationService.navigateToLink(link);
        }
    }

    setWatchers() {
        this.notificationsService.connectionChange$
            .pipe(
                // Add a small debounce for instabilities during reconnections.
                debounceTime(200),
                takeUntil(this.ngUnsubscribe$)
            )
            .subscribe(status => {
                if (
                    status === 'reconnected' &&
                    this.websocketConnectionStatus === 'disconnected'
                ) {
                    this.websocketConnectionStatus = status;
                    this.refreshNotifications(false);
                } else {
                    this.websocketConnectionStatus = status;
                }
            });
    }
}
